import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { commissionsAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { DollarSign, TrendingUp, CheckCircle, Clock, Percent, Pencil, CalendarDays, AlertCircle, XCircle, AlertTriangle, Info, Wrench, Download, Loader2, ShieldAlert } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { cn } from "../lib/utils";

export default function Comisiones() {
  const { user, isAdmin, isVendedor } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Edit date dialog state
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [editDate, setEditDate] = useState("");
  
  // Status details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);

  // Admin commission migration tool (IVA fix)
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [migrationHistory, setMigrationHistory] = useState(null);
  const [migrationLoadingHistory, setMigrationLoadingHistory] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyConfirmed, setApplyConfirmed] = useState(false);

  // Commission status configuration
  const statusConfig = {
    sin_pago: {
      label: "Sin Pago",
      color: "bg-slate-100 text-slate-700",
      icon: Clock,
      description: "Contrato sin pagos registrados"
    },
    monto_insuficiente: {
      label: "Monto Insuficiente",
      color: "bg-amber-100 text-amber-800",
      icon: AlertTriangle,
      description: "Pagos no alcanzan el 20% del contrato"
    },
    devengada: {
      label: "Devengada",
      color: "bg-blue-100 text-blue-800",
      icon: CheckCircle,
      description: "Umbral 20% alcanzado, pendiente de pago"
    },
    pagada: {
      label: "Pagada",
      color: "bg-green-100 text-green-800",
      icon: DollarSign,
      description: "Comisión pagada al vendedor"
    },
    cancelada_sin_derecho: {
      label: "Cancelada (Sin Derecho)",
      color: "bg-red-100 text-red-800",
      icon: XCircle,
      description: "Contrato cancelado antes del 20%"
    }
  };

  useEffect(() => {
    fetchData();
  }, [vendorFilter, statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (vendorFilter !== "all") params.vendor_id = vendorFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      
      const [commissionsRes, vendedoresRes] = await Promise.all([
        commissionsAPI.getAll(params),
        // v4.54.0: include_inactive=true para que el filtro histórico de Comisiones
        // incluya vendedores desactivados con sufijo "(inactivo)".
        isAdmin ? usersAPI.getVendedores({ include_inactive: true }) : Promise.resolve({ data: [] })
      ]);
      
      setCommissions(commissionsRes.data);
      if (isAdmin) {
        setVendors((vendedoresRes.data || []).map(v => ({
          id: v.id,
          name: v.name,
          role: v.role,
          is_active: v.is_active !== false,
        })));
      }
    } catch (error) {
      toast.error("Error al cargar comisiones");
    } finally {
      setLoading(false);
    }
  };

  const handlePayCommission = async (id) => {
    try {
      await commissionsAPI.pay(id);
      toast.success("Comisión pagada exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al pagar comisión");
    }
  };

  const openDetailsDialog = (commission) => {
    setSelectedCommission(commission);
    setDetailsDialogOpen(true);
  };

  const openEditDateDialog = (commission) => {
    setEditingCommission(commission);
    // Pre-fill with existing date if available
    if (commission.commission_paid_date) {
      setEditDate(commission.commission_paid_date.split("T")[0]);
    } else {
      setEditDate("");
    }
    setEditDateDialogOpen(true);
  };

  const handleUpdatePaidDate = async () => {
    if (!editingCommission || !editDate) {
      toast.error("Selecciona una fecha válida");
      return;
    }
    try {
      await commissionsAPI.updatePaidDate(editingCommission.id, editDate);
      toast.success("Fecha de pago actualizada");
      setEditDateDialogOpen(false);
      setEditingCommission(null);
      setEditDate("");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar fecha de pago");
    }
  };

  // ====== Admin Commission Migration Tool ======
  const fetchMigrationHistory = async () => {
    setMigrationLoadingHistory(true);
    try {
      const res = await commissionsAPI.migrationHistory();
      setMigrationHistory(res.data);
    } catch (error) {
      toast.error("No se pudo cargar el historial de migración");
      setMigrationHistory(null);
    } finally {
      setMigrationLoadingHistory(false);
    }
  };

  const openMigrationDialog = () => {
    setDryRunResult(null);
    setApplyResult(null);
    setApplyConfirmed(false);
    setMigrationDialogOpen(true);
    fetchMigrationHistory();
  };

  const closeMigrationDialog = () => {
    setMigrationDialogOpen(false);
    setDryRunResult(null);
    setApplyResult(null);
    setApplyConfirmed(false);
  };

  const handleDryRun = async () => {
    setDryRunLoading(true);
    setApplyResult(null);
    setApplyConfirmed(false);
    try {
      const res = await commissionsAPI.migrateDryRun();
      setDryRunResult(res.data);
      toast.success("Dry-run completado. Revisa los resultados antes de aplicar.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error ejecutando dry-run");
    } finally {
      setDryRunLoading(false);
    }
  };

  const handleApplyMigration = async () => {
    if (!dryRunResult) {
      toast.error("Debes ejecutar el dry-run antes de aplicar");
      return;
    }
    if (!applyConfirmed) {
      toast.error("Debes marcar la confirmación explícita antes de aplicar");
      return;
    }
    setApplyLoading(true);
    try {
      const res = await commissionsAPI.migrateApply();
      setApplyResult(res.data);
      toast.success(`Migración aplicada: ${res.data.applied_count} comisiones actualizadas`);
      // Refresh banner + commissions table
      fetchMigrationHistory();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error aplicando migración");
    } finally {
      setApplyLoading(false);
    }
  };

  const downloadCsvReport = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const url = commissionsAPI.migrateCsvUrl(type);
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Error descargando CSV");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `commissions_migration_${type}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`CSV ${type} descargado`);
    } catch (error) {
      toast.error("Error descargando CSV");
    }
  };

  const fmtMoney = (n) => `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Helper to format paid date
  const formatPaidDate = (commission) => {
    if (commission.status !== "pagada") {
      return <span className="text-slate-400 italic">Pendiente</span>;
    }
    if (commission.commission_paid_date) {
      return new Date(commission.commission_paid_date).toLocaleDateString("es-MX");
    }
    // Fallback to paid_at if commission_paid_date not set
    if (commission.paid_at) {
      return new Date(commission.paid_at).toLocaleDateString("es-MX");
    }
    return <span className="text-slate-400 italic">Sin fecha</span>;
  };

  const totalSinPago = commissions.filter(c => c.status === "sin_pago").reduce((sum, c) => sum + c.commission_amount, 0);
  const totalMontoInsuficiente = commissions.filter(c => c.status === "monto_insuficiente").reduce((sum, c) => sum + c.commission_amount, 0);
  const totalDevengadas = commissions.filter(c => c.status === "devengada").reduce((sum, c) => sum + c.commission_amount, 0);
  const totalPagadas = commissions.filter(c => c.status === "pagada").reduce((sum, c) => sum + c.commission_amount, 0);
  const totalGeneral = commissions.filter(c => c.status !== "cancelada_sin_derecho").reduce((sum, c) => sum + c.commission_amount, 0);
  const countSinPago = commissions.filter(c => c.status === "sin_pago").length;
  const countActive = commissions.filter(c => c.status !== "cancelada_sin_derecho").length;

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="comisiones-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Comisiones</h1>
            <p className="text-slate-500 mt-1">
              {isVendedor ? "Tus comisiones por ventas" : "Gestión de comisiones de vendedores"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              La comisión se devenga cuando los pagos del cliente alcanzan el 20% del total del contrato
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={openMigrationDialog}
              data-testid="open-migrate-commissions-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Migrar comisiones legacy
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="card-hover border-l-4 border-l-slate-400">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Sin Pago</p>
                  <p className="text-xl font-bold text-slate-600">${totalSinPago.toLocaleString("es-MX")}</p>
                  <p className="text-xs text-slate-400">{countSinPago} contratos</p>
                </div>
                <Clock className="w-6 h-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Monto Insuficiente</p>
                  <p className="text-xl font-bold text-amber-600">${totalMontoInsuficiente.toLocaleString("es-MX")}</p>
                  <p className="text-xs text-slate-400">{commissions.filter(c => c.status === "monto_insuficiente").length} contratos</p>
                </div>
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Devengado (Por Pagar)</p>
                  <p className="text-xl font-bold text-blue-600">${totalDevengadas.toLocaleString("es-MX")}</p>
                  <p className="text-xs text-slate-400">{commissions.filter(c => c.status === "devengada").length} contratos</p>
                </div>
                <Clock className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Pagado</p>
                  <p className="text-xl font-bold text-green-600">${totalPagadas.toLocaleString("es-MX")}</p>
                  <p className="text-xs text-slate-400">{commissions.filter(c => c.status === "pagada").length} contratos</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total General</p>
                  <p className="text-xl font-bold text-slate-900">${totalGeneral.toLocaleString("es-MX")}</p>
                  <p className="text-xs text-slate-400">{countActive} contratos</p>
                </div>
                <TrendingUp className="w-6 h-6 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        {isAdmin && (
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="vendor-filter">
                    <SelectValue placeholder="Vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los vendedores</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id} data-testid={`vendor-filter-item-${v.id}`}>
                        {v.name}{v.is_active === false ? " (inactivo)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="sin_pago">Sin Pago</SelectItem>
                    <SelectItem value="monto_insuficiente">Monto Insuficiente</SelectItem>
                    <SelectItem value="devengada">Devengadas</SelectItem>
                    <SelectItem value="pagada">Pagadas</SelectItem>
                    <SelectItem value="cancelada_sin_derecho">Canceladas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : commissions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Percent className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay contratos con vendedor asignado para este filtro</p>
                <p className="text-xs text-slate-400 mt-1">Los contratos aparecen aquí cuando tienen un vendedor con tasa de comisión configurada</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Contrato</TableHead>
                    <TableHead>Total Contrato</TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1" title="La comisión se calcula sobre el subtotal/base sin IVA. El umbral de devengamiento se mantiene sobre el total cobrado.">
                        Base sin IVA
                        <Info className="w-3 h-3 text-slate-400" />
                      </span>
                    </TableHead>
                    <TableHead>Pagos (20%)</TableHead>
                    <TableHead>Tasa</TableHead>
                    <TableHead>Comisión</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <CalendarDays className="w-4 h-4" />
                        Fecha Pago
                      </div>
                    </TableHead>
                    {isAdmin && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.map((commission) => {
                    const config = statusConfig[commission.status] || statusConfig.monto_insuficiente;
                    const StatusIcon = config.icon;
                    const progress = commission.threshold_required > 0 
                      ? Math.min(100, (commission.payments_accumulated / commission.threshold_required) * 100) 
                      : 0;
                    
                    return (
                      <TableRow 
                        key={commission.id} 
                        data-testid={`commission-row-${commission.id}`}
                        className={cn(
                          commission.status === "cancelada_sin_derecho" && "bg-red-50 opacity-75"
                        )}
                      >
                        <TableCell>
                          <div className="text-sm font-medium">{commission.vendor_name || commission.agent_name || "N/A"}</div>
                          <div className="text-xs text-slate-400">{commission.client_name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-mono text-xs">{commission.contract_folio || commission.contract_id?.slice(0, 8)}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">${commission.contract_total_mxn?.toLocaleString("es-MX") || commission.contract_total?.toLocaleString("es-MX")}</div>
                          {commission.contract_currency === "USD" && (
                            <div className="text-xs text-slate-400">USD ${commission.contract_total?.toLocaleString("es-MX")}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {commission.commission_base_mxn !== undefined && commission.commission_base_mxn !== null ? (
                            <div>
                              <div className="font-medium text-slate-700">${commission.commission_base_mxn?.toLocaleString("es-MX")}</div>
                              <div className="text-[10px] text-slate-400">subtotal sin IVA</div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic" title="Comisión calculada antes del fix v4.41 (base = total con IVA)">
                              legacy
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">${commission.payments_accumulated?.toLocaleString("es-MX") || 0}</span>
                              <span className="text-xs text-slate-400">/ ${commission.threshold_required?.toLocaleString("es-MX")}</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-1.5">
                              <div 
                                className={cn(
                                  "h-1.5 rounded-full transition-all",
                                  commission.threshold_met ? "bg-green-500" : "bg-amber-500"
                                )}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            {commission.threshold_met && (
                              <div className="text-xs text-green-600">✓ Umbral alcanzado</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {commission.missing_commission_rate ? (
                            <Badge variant="outline" className="border-red-300 text-red-600 text-xs">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Sin tasa
                            </Badge>
                          ) : (
                            <Badge variant="outline">{commission.commission_rate}%</Badge>
                          )}
                        </TableCell>
                        <TableCell className={cn(
                          "font-medium",
                          commission.status === "cancelada_sin_derecho" ? "text-red-400 line-through" : "text-green-600"
                        )}>
                          ${commission.commission_amount?.toLocaleString("es-MX")}
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => openDetailsDialog(commission)}
                            className="cursor-pointer"
                          >
                            <Badge className={cn(config.color, "flex items-center gap-1")}>
                              <StatusIcon className="w-3 h-3" />
                              {config.label}
                            </Badge>
                          </button>
                        </TableCell>
                        <TableCell data-testid={`commission-paid-date-${commission.id}`}>
                          <div className="flex items-center gap-2">
                            {formatPaidDate(commission)}
                            {isAdmin && commission.status === "pagada" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => openEditDateDialog(commission)}
                                data-testid={`edit-paid-date-${commission.id}`}
                              >
                                <Pencil className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {commission.status === "devengada" && (
                              <Button
                                size="sm"
                                onClick={() => handlePayCommission(commission.id)}
                                data-testid={`pay-commission-${commission.id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Pagar
                              </Button>
                            )}
                            {commission.status === "monto_insuficiente" && (
                              <span className="text-xs text-amber-500">Esperando pagos</span>
                            )}
                            {commission.status === "sin_pago" && (
                              <span className="text-xs text-slate-400">Sin pagos</span>
                            )}
                          </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Paid Date Dialog */}
      <Dialog open={editDateDialogOpen} onOpenChange={setEditDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Fecha de Pago de Comisión</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Modificar la fecha en que TenaTour pagó esta comisión al vendedor.
            </p>
            <Input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              data-testid="edit-paid-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdatePaidDate} data-testid="save-paid-date-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalles de Comisión</DialogTitle>
          </DialogHeader>
          {selectedCommission && (
            <div className="space-y-4">
              {/* Contract Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-sm text-slate-700 mb-2">Información del Contrato</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Folio:</span>
                    <span className="ml-2 font-mono">{selectedCommission.contract_folio || selectedCommission.contract_id?.slice(0, 8)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Total:</span>
                    <span className="ml-2 font-medium">${selectedCommission.contract_total_mxn?.toLocaleString("es-MX")}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Vendedor:</span>
                    <span className="ml-2">{selectedCommission.vendor_name || selectedCommission.agent_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <span className="ml-2">{selectedCommission.client_name}</span>
                  </div>
                </div>
              </div>

              {/* Threshold Info */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-sm text-blue-700 mb-2">Umbral de Comisión (20%)</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Requerido:</span>
                    <span className="font-medium">${selectedCommission.threshold_required?.toLocaleString("es-MX")}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Pagos acumulados:</span>
                    <span className="font-medium">${selectedCommission.payments_accumulated?.toLocaleString("es-MX")}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className={cn(
                        "h-2 rounded-full transition-all",
                        selectedCommission.threshold_met ? "bg-green-500" : "bg-amber-500"
                      )}
                      style={{ 
                        width: `${Math.min(100, (selectedCommission.payments_accumulated / selectedCommission.threshold_required) * 100)}%` 
                      }}
                    />
                  </div>
                  {selectedCommission.threshold_met_at && (
                    <div className="text-xs text-green-600">
                      ✓ Umbral alcanzado: {new Date(selectedCommission.threshold_met_at).toLocaleString("es-MX")}
                    </div>
                  )}
                </div>
              </div>

              {/* Commission Amount */}
              <div className="p-3 bg-green-50 rounded-lg">
                <h4 className="font-medium text-sm text-green-700 mb-2">Comisión</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Base sin IVA:</span>
                    <span className="font-medium">
                      {selectedCommission.commission_base_mxn !== undefined && selectedCommission.commission_base_mxn !== null
                        ? `$${selectedCommission.commission_base_mxn?.toLocaleString("es-MX")}`
                        : <span className="italic text-slate-400" title="Comisión calculada antes del fix v4.41">legacy (sin desglose)</span>}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tasa:</span>
                    <span className="font-medium">{selectedCommission.commission_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-green-200">
                    <span className="text-slate-700 font-medium">Comisión:</span>
                    <div className={cn(
                      "text-xl font-bold",
                      selectedCommission.status === "cancelada_sin_derecho" ? "text-red-400 line-through" : "text-green-600"
                    )}>
                      ${selectedCommission.commission_amount?.toLocaleString("es-MX")}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 italic pt-1">
                    La comisión se calcula sobre el subtotal/base sin IVA. El umbral de devengamiento se mantiene sobre el total cobrado.
                  </p>
                </div>
              </div>

              {/* Status History */}
              {selectedCommission.status_history?.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-700 mb-2">Historial de Estados</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCommission.status_history.map((entry, idx) => (
                      <div key={idx} className="text-xs border-l-2 border-slate-300 pl-2">
                        <div className="font-medium">{statusConfig[entry.status]?.label || entry.status}</div>
                        <div className="text-slate-500">{entry.reason}</div>
                        <div className="text-slate-400">
                          {new Date(entry.timestamp).toLocaleString("es-MX")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancellation Note */}
              {selectedCommission.cancellation_note && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">{selectedCommission.cancellation_note}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Commission Migration Dialog (IVA fix) */}
      <Dialog open={migrationDialogOpen} onOpenChange={(o) => (o ? setMigrationDialogOpen(true) : closeMigrationDialog())}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="migration-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Migrar Comisiones Legacy (Fix IVA)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Banner: anti-duplicación */}
            <div
              className={cn(
                "p-3 rounded-lg border text-sm",
                migrationHistory?.has_previous_migration
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-slate-50 border-slate-200 text-slate-700"
              )}
              data-testid="migration-history-banner"
            >
              {migrationLoadingHistory ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verificando historial de migración…</span>
                </div>
              ) : migrationHistory?.has_previous_migration ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 font-medium">
                    <ShieldAlert className="w-4 h-4" />
                    Ya existe una migración previa aplicada
                  </div>
                  <div className="text-xs space-y-0.5 mt-1">
                    <div><span className="text-amber-700">Fecha/hora:</span> {new Date(migrationHistory.last_migration_at).toLocaleString("es-MX")}</div>
                    <div><span className="text-amber-700">Usuario:</span> {migrationHistory.last_migration_by_name || migrationHistory.last_migration_by_id || "—"}</div>
                    <div><span className="text-amber-700">Comisiones migradas:</span> {migrationHistory.migrated_count}</div>
                    {migrationHistory.backup_collection && (
                      <div><span className="text-amber-700">Backup:</span> <code className="font-mono text-[11px]">{migrationHistory.backup_collection}</code></div>
                    )}
                  </div>
                  <p className="text-[11px] text-amber-700 mt-1 italic">
                    Volver a aplicar es idempotente: las comisiones ya migradas serán omitidas. Aun así, evita ejecuciones repetidas innecesarias.
                  </p>
                </div>
              ) : (
                <div className="text-sm">Aún no se ha ejecutado ninguna migración.</div>
              )}
            </div>

            {/* Description */}
            <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
              <p className="font-medium text-slate-700 mb-1">¿Qué hace esta herramienta?</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Recalcula las comisiones legacy sobre el <strong>subtotal sin IVA</strong> (correcto) en lugar del total con IVA.</li>
                <li>Las comisiones <strong>pagadas</strong> nunca se modifican; solo se reportan diferencias en <code>paid_diff</code>.</li>
                <li>El flujo es estricto: primero <strong>Dry-run</strong>, luego <strong>Aplicar</strong> con confirmación explícita.</li>
                <li>Al aplicar, se crea automáticamente un snapshot de respaldo en una colección backup.</li>
              </ul>
            </div>

            {/* Step 1: Dry-run */}
            <div className="border rounded-lg p-4 space-y-3" data-testid="migration-step-dryrun">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-sm">Paso 1 · Dry-run (sin escrituras)</h4>
                  <p className="text-xs text-slate-500">Genera un reporte sin modificar la base de datos.</p>
                </div>
                <Button
                  onClick={handleDryRun}
                  disabled={dryRunLoading}
                  data-testid="migration-dryrun-btn"
                >
                  {dryRunLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {dryRunResult ? "Re-ejecutar Dry-run" : "Ejecutar Dry-run"}
                </Button>
              </div>

              {dryRunResult && (
                <div className="space-y-3" data-testid="dryrun-summary">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 bg-slate-50 rounded">
                      <div className="text-slate-500">Revisadas</div>
                      <div className="text-lg font-bold">{dryRunResult.summary.total_reviewed}</div>
                    </div>
                    <div className="p-2 bg-blue-50 rounded">
                      <div className="text-blue-600">A actualizar</div>
                      <div className="text-lg font-bold text-blue-700" data-testid="would-update-count">{dryRunResult.summary.would_update}</div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded">
                      <div className="text-amber-600">Pagadas (solo reporte)</div>
                      <div className="text-lg font-bold text-amber-700">{dryRunResult.summary.paid_diff_reported}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <div className="text-slate-500">Sin cambios</div>
                      <div className="text-lg font-bold">{dryRunResult.unchanged_count}</div>
                    </div>
                  </div>

                  {/* Classification breakdown */}
                  <div className="border-t pt-3 space-y-2">
                    <div className="text-xs font-medium text-slate-700">Clasificación de comisiones a actualizar</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="p-2 bg-emerald-50 border border-emerald-200 rounded" data-testid="iva-fix-bucket">
                        <div className="text-emerald-700 font-medium">IVA fix</div>
                        <div className="text-lg font-bold text-emerald-800">{dryRunResult.summary.iva_fix_count || 0}</div>
                        <div className="text-[10px] text-emerald-700">Δ {fmtMoney(dryRunResult.summary.iva_fix_delta || 0)}</div>
                      </div>
                      <div className="p-2 bg-rose-50 border border-rose-200 rounded" data-testid="partial-payment-bucket">
                        <div className="text-rose-700 font-medium">Pago parcial fix</div>
                        <div className="text-lg font-bold text-rose-800">{dryRunResult.summary.partial_payment_fix_count || 0}</div>
                        <div className="text-[10px] text-rose-700">Δ {fmtMoney(dryRunResult.summary.partial_payment_fix_delta || 0)}</div>
                      </div>
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded" data-testid="unknown-bucket">
                        <div className="text-slate-700 font-medium">Sin clasificar</div>
                        <div className="text-lg font-bold text-slate-800">{dryRunResult.summary.unknown_count || 0}</div>
                        <div className="text-[10px] text-slate-700">Δ {fmtMoney(dryRunResult.summary.unknown_delta || 0)}</div>
                      </div>
                      <div className="p-2 bg-slate-100 border border-slate-200 rounded" data-testid="already-correct-bucket">
                        <div className="text-slate-700 font-medium">Ya correctas</div>
                        <div className="text-lg font-bold text-slate-800">{dryRunResult.summary.already_correct_count || 0}</div>
                        <div className="text-[10px] text-slate-600">sin cambio</div>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 italic">
                      <strong>IVA fix</strong>: legacy calculaba sobre total con IVA (delta ~−13.79% si IVA 16%). <strong>Pago parcial fix</strong>: legacy calculaba sobre un solo pago, no sobre el contrato (deltas grandes). <strong>Sin clasificar</strong>: requiere revisión manual.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs border-t pt-2">
                    <div>
                      <div className="text-slate-500">Total anterior</div>
                      <div className="font-medium">{fmtMoney(dryRunResult.summary.total_amount_old)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Total nuevo</div>
                      <div className="font-medium">{fmtMoney(dryRunResult.summary.total_amount_new)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Delta</div>
                      <div className={cn("font-bold", dryRunResult.summary.delta_total < 0 ? "text-red-600" : "text-green-600")}>
                        {fmtMoney(dryRunResult.summary.delta_total)}
                      </div>
                    </div>
                  </div>
                  {dryRunResult.errors?.length > 0 && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      ⚠ {dryRunResult.errors.length} errores detectados (contratos no encontrados, etc.)
                    </div>
                  )}

                  {/* CSV downloads */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsvReport("would_update")}
                      disabled={dryRunResult.summary.would_update === 0}
                      data-testid="download-csv-would-update"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV would_update ({dryRunResult.summary.would_update})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsvReport("iva_fix")}
                      disabled={(dryRunResult.summary.iva_fix_count || 0) === 0}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="download-csv-iva-fix"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV iva_fix ({dryRunResult.summary.iva_fix_count || 0})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsvReport("per_payment")}
                      disabled={(dryRunResult.summary.partial_payment_fix_count || 0) === 0}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid="download-csv-per-payment"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV per_payment ({dryRunResult.summary.partial_payment_fix_count || 0})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsvReport("unknown")}
                      disabled={(dryRunResult.summary.unknown_count || 0) === 0}
                      data-testid="download-csv-unknown"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV unknown ({dryRunResult.summary.unknown_count || 0})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsvReport("paid_diff")}
                      disabled={dryRunResult.summary.paid_diff_reported === 0}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="download-csv-paid-diff"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV paid_diff ({dryRunResult.summary.paid_diff_reported})
                    </Button>
                  </div>

                  {/* Detalle (primeras 5) */}
                  {dryRunResult.would_update?.length > 0 && (
                    <div className="border rounded p-2 max-h-48 overflow-y-auto text-xs">
                      <div className="font-medium text-slate-600 mb-1">Vista previa (máx. 5 filas)</div>
                      <table className="w-full text-[11px]">
                        <thead className="text-slate-500">
                          <tr>
                            <th className="text-left">Folio</th>
                            <th className="text-left">Vendedor</th>
                            <th className="text-left">Tipo</th>
                            <th className="text-right">Anterior</th>
                            <th className="text-right">Nuevo</th>
                            <th className="text-right">Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dryRunResult.would_update.slice(0, 5).map((r) => (
                            <tr key={r.commission_id} className="border-t">
                              <td className="font-mono">{r.contract_folio}</td>
                              <td>{r.vendor_name}</td>
                              <td>
                                <span className={cn(
                                  "px-1.5 py-0.5 rounded text-[10px] font-medium",
                                  r.legacy_type === "total_with_iva" && "bg-emerald-100 text-emerald-700",
                                  r.legacy_type === "per_payment" && "bg-rose-100 text-rose-700",
                                  r.legacy_type === "unknown" && "bg-slate-200 text-slate-700",
                                  r.legacy_type === "already_subtotal" && "bg-blue-100 text-blue-700"
                                )}>
                                  {r.legacy_type}
                                </span>
                              </td>
                              <td className="text-right">{fmtMoney(r.old_amount)}</td>
                              <td className="text-right">{fmtMoney(r.new_amount)}</td>
                              <td className={cn("text-right font-medium", r.delta < 0 ? "text-red-600" : "text-green-600")}>
                                {fmtMoney(r.delta)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Apply */}
            <div
              className={cn(
                "border rounded-lg p-4 space-y-3",
                !dryRunResult && "opacity-50 pointer-events-none"
              )}
              data-testid="migration-step-apply"
            >
              <div>
                <h4 className="font-medium text-sm flex items-center gap-2">
                  Paso 2 · Aplicar migración
                  {!dryRunResult && <span className="text-xs text-slate-400">(disponible tras el dry-run)</span>}
                </h4>
                <p className="text-xs text-slate-500">
                  Esta acción modifica registros en la base de datos. Solo afecta comisiones <strong>no pagadas</strong>. Crea un snapshot de respaldo automáticamente.
                </p>
              </div>

              {applyResult ? (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1" data-testid="apply-result">
                  <div className="flex items-center gap-2 font-medium text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    Migración aplicada exitosamente
                  </div>
                  <div className="text-xs text-green-800 space-y-0.5">
                    <div>Comisiones actualizadas: <strong>{applyResult.applied_count}</strong></div>
                    <div>Backup creado en: <code className="font-mono text-[11px]">{applyResult.backup_collection}</code></div>
                    <div>Ejecutado por: {applyResult.performed_by}</div>
                    <div>Fecha: {new Date(applyResult.performed_at).toLocaleString("es-MX")}</div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2">
                    <Checkbox
                      id="apply-confirm-checkbox"
                      checked={applyConfirmed}
                      onCheckedChange={(v) => setApplyConfirmed(!!v)}
                      disabled={!dryRunResult || dryRunResult?.summary?.would_update === 0}
                      data-testid="apply-confirm-checkbox"
                    />
                    <label htmlFor="apply-confirm-checkbox" className="text-xs text-amber-900 cursor-pointer">
                      Confirmo explícitamente que revisé el dry-run y autorizo la migración de
                      <strong> {dryRunResult?.summary?.would_update || 0}</strong> comisión(es) sobre la base sin IVA.
                      Las comisiones pagadas no serán modificadas.
                    </label>
                  </div>
                  <Button
                    variant="default"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleApplyMigration}
                    disabled={
                      !dryRunResult ||
                      !applyConfirmed ||
                      applyLoading ||
                      dryRunResult?.summary?.would_update === 0
                    }
                    data-testid="migration-apply-btn"
                  >
                    {applyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
                    Aplicar migración ({dryRunResult?.summary?.would_update || 0})
                  </Button>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeMigrationDialog} data-testid="close-migration-dialog-btn">
              {applyResult ? "Cerrar" : "Cancelar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
